import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChefHat, HandHeart, Home as HomeIcon } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

// The three platforms, Heart of House first. Logo heights are multiples of the MiSE wordmark height measured in
// each file (total height / wordmark band height), so the wordmarks render the same size.
const PLATFORMS = [
  {
    key: "heartOfHouse",
    logo: "/images/logos/heart-of-house.png",
    heightPerWordmark: 1.544,
    info: "/heart-of-house",
    site: "heartofhouse.ca",
    icon: ChefHat,
  },
  {
    key: "familyMeals",
    logo: "/images/logos/family-meals.png",
    heightPerWordmark: 1.873,
    info: "/family-meals",
    site: "misefamilymeals.ca",
    icon: HandHeart,
  },
  {
    key: "atHome",
    logo: "/images/logos/at-home.png",
    heightPerWordmark: 1.791,
    info: "/at-home",
    site: "misehome.ca",
    icon: HomeIcon,
  },
];

// Every section under the hero shares this narrower width.
const NARROW = "mx-auto max-w-5xl";

const Eyebrow = ({ children, className = "" }) => (
  <p className={`text-[11px] font-semibold tracking-[0.22em] uppercase text-[#E8490F] ${className}`}>{children}</p>
);

const Home = () => {
  const { t } = useLanguage();
  const home = t.home;
  const [wasteStat, insecureStat, marginStat] = home.stats;

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute left-1/2 -top-56 h-[440px] w-[640px] -translate-x-1/2 rounded-full bg-[#E8490F]/15 blur-3xl"
          aria-hidden
        />
        <div className="mise-container relative pt-10 md:pt-12 pb-12 md:pb-14 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#E8490F]/40 bg-[#E8490F]/10 px-4 py-1.5 text-[10.5px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]" data-testid="hero-eyebrow">
            <span className="h-[7px] w-[7px] rounded-full bg-[#E8490F] shadow-[0_0_0_4px_rgba(232,73,15,0.2)]" />
            {home.heroEyebrow}
          </span>
          <h1
            className="mt-4 mx-auto max-w-4xl text-4xl sm:text-5xl lg:text-[3.1rem] font-semibold tracking-tight leading-[1.04] [text-wrap:balance]"
            data-testid="hero-tagline"
          >
            {home.heroLineA}
            <span className="block mt-1 mise-serif mise-accent text-[0.85em]">{home.heroLineBItalic}</span>
          </h1>
          <p className="mt-4 mx-auto max-w-3xl text-base md:text-[17px] text-white/70 leading-relaxed">{home.heroMission}</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/about" className="mise-pill-primary" data-testid="hero-cta-primary" data-track="hero-cta-primary">
              {home.heroCtaPrimary}
              <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="mise-pill-secondary" data-testid="hero-cta-secondary" data-track="hero-cta-secondary">
              {home.heroCtaSecondary}
            </Link>
          </div>

          <div className="mt-9 mx-auto max-w-5xl grid gap-4 md:grid-cols-3" data-testid="hero-pillars">
            {PLATFORMS.map((p) => {
              const pillar = home.pillars[p.key];
              const hoh = p.key === "heartOfHouse";
              return (
                <Link
                  key={p.key}
                  to={p.info}
                  className={`group flex flex-col items-center rounded-2xl border px-5 py-5 text-center transition-colors ${
                    hoh
                      ? "border-[#E8490F]/40 bg-gradient-to-b from-[#2b1810] to-[#181818] hover:border-[#E8490F]/70"
                      : "border-[#333333] bg-[#181818] hover:border-[#E8490F]/50"
                  }`}
                  data-testid={`hero-pillar-${p.key}`}
                  data-track={`hero-pillar-${p.key}`}
                >
                  <span className="flex h-[3.9rem] items-center justify-center">
                    <img
                      src={p.logo}
                      alt={home.platforms[p.key].name}
                      style={{ height: `calc(2rem * ${p.heightPerWordmark})` }}
                      className="w-auto max-w-full"
                    />
                  </span>
                  <p className="mt-4 text-[11px] font-semibold tracking-[0.2em] uppercase text-[#E8490F]">{pillar.verb}</p>
                  <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{pillar.text}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* IT ALL CONNECTS */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="section-connect">
        <div className="mise-container"><div className={NARROW}>
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-end">
            <div className="lg:col-span-5">
              <Eyebrow>{home.connectEyebrow}</Eyebrow>
              <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight text-white">{home.connectHeading}</h2>
            </div>
            <p className="lg:col-span-7 text-sm md:text-base text-white/65 leading-relaxed">{home.connectSub}</p>
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-stretch">
            {home.connectNodes.map((node, i) => (
              <React.Fragment key={node.title}>
                {i > 0 && (
                  <span className="hidden md:flex items-center justify-center px-1 text-[#E8490F]" aria-hidden>
                    <ArrowRight size={18} />
                  </span>
                )}
                <div className="rounded-xl border border-[#333333] bg-[#181818] p-4" data-testid={`connect-node-${i}`}>
                  <p className="text-[10px] font-semibold tracking-[0.22em] uppercase text-[#E8490F]">
                    0{i + 1} · {node.platform}
                  </p>
                  <h3 className="mt-1.5 text-[15px] font-semibold text-white">{node.title}</h3>
                  <p className="mt-1 text-[13px] text-white/60 leading-relaxed">{node.text}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div></div>
      </section>

      {/* WHAT WE BELIEVE */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="section-believe">
        <div className="mise-container"><div className={NARROW}>
          <Eyebrow>{home.believeEyebrow}</Eyebrow>
          <ol className="mt-4">
            {home.beliefs.map((b, i) => (
              <li
                key={b.title}
                className="grid gap-1.5 md:gap-6 md:grid-cols-[52px_1fr_1.35fr] md:items-baseline border-t border-[#2a2a2a] py-4"
                data-testid={`belief-${i}`}
              >
                <span className="mise-serif mise-accent text-2xl leading-none">0{i + 1}</span>
                <h3 className="text-base md:text-lg font-semibold text-white">{b.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{b.text}</p>
              </li>
            ))}
          </ol>
        </div></div>
      </section>

      {/* VISION */}
      <section className="py-12 md:py-16 border-t border-[#242424] text-center" data-testid="section-vision">
        <div className="mise-container"><div className={NARROW}>
          <Eyebrow>{home.visionEyebrow}</Eyebrow>
          <p className="mt-3 mx-auto max-w-3xl mise-serif mise-accent text-2xl md:text-[2.1rem] leading-[1.2] [text-wrap:balance]">
            {home.vision}
          </p>
        </div></div>
      </section>

      {/* TWO SIDES OF ONE TABLE */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="section-sides">
        <div className="mise-container"><div className={NARROW}>
          <Eyebrow>{home.sidesEyebrow}</Eyebrow>
          <h2 className="mt-3 max-w-3xl text-2xl md:text-3xl font-semibold tracking-tight text-white leading-snug [text-wrap:balance]">
            {home.sidesHeading}
          </h2>
          <div className="mt-7 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-[#E8490F]/40 bg-gradient-to-br from-[#2b1810] to-[#181818] p-6" data-testid="sides-operators">
              <Eyebrow>{home.sidesOpsEyebrow}</Eyebrow>
              <h3 className="mt-2 text-lg font-semibold text-white">{home.sidesOpsTitle}</h3>
              <Stat stat={marginStat} />
              <p className="mt-2 text-[11px] text-white/40">{marginStat.source}</p>
              <div className="mt-5 pt-4 border-t border-[#2e2e2e] flex flex-wrap items-center gap-2 text-sm text-white/65">
                {home.sidesAnswer}
                <AnswerChip to="/heart-of-house" icon={ChefHat} label={home.platforms.heartOfHouse.name} />
                {home.sidesOpsAfter}
              </div>
            </div>
            <div className="rounded-2xl border border-[#333333] bg-[#181818] p-6" data-testid="sides-community">
              <Eyebrow>{home.sidesCommunityEyebrow}</Eyebrow>
              <h3 className="mt-2 text-lg font-semibold text-white">{home.sidesCommunityTitle}</h3>
              <div className="grid grid-cols-2 gap-x-6">
                <Stat stat={wasteStat} />
                <Stat stat={insecureStat} />
              </div>
              <p className="mt-2 text-[11px] text-white/40">
                {wasteStat.source} · {insecureStat.source}
              </p>
              <div className="mt-5 pt-4 border-t border-[#2e2e2e] flex flex-wrap items-center gap-2 text-sm text-white/65">
                {home.sidesAnswer}
                <AnswerChip to="/family-meals" icon={HandHeart} label={home.platforms.familyMeals.name} />
                <AnswerChip to="/at-home" icon={HomeIcon} label={home.platforms.atHome.name} />
              </div>
            </div>
          </div>
        </div></div>
      </section>

      {/* PLATFORMS: one row of three, each with Info (the page here) and the product's own site */}
      <section className="py-12 md:py-16 border-t border-[#242424]" data-testid="section-platforms">
        <div className="mise-container"><div className={NARROW}>
          <Eyebrow>{home.platformsEyebrow}</Eyebrow>
          <h2 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight text-white">{home.platformsHeading}</h2>
          <div className="mt-7 grid gap-4 md:grid-cols-3">
            {PLATFORMS.map((p) => {
              const copy = home.platforms[p.key];
              const hoh = p.key === "heartOfHouse";
              return (
                <div
                  key={p.key}
                  className={`flex flex-col rounded-2xl border p-5 ${
                    hoh ? "border-[#E8490F]/45 bg-gradient-to-br from-[#2b1810] to-[#181818]" : "border-[#333333] bg-[#181818]"
                  }`}
                  data-testid={`platform-card-${p.key}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-16 w-24 flex-shrink-0 items-center justify-center rounded-xl border border-[#2a2a2a] bg-[#0d0d0d]">
                      <img
                        src={p.logo}
                        alt={copy.name}
                        style={{ height: `calc(1.55rem * ${p.heightPerWordmark})` }}
                        className="w-auto max-w-[80px]"
                      />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] tracking-[0.18em] uppercase text-white/40">{copy.tag}</p>
                      <h3 className="mt-1 font-semibold text-white leading-tight">{copy.name}</h3>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-white/65 leading-relaxed flex-1">{copy.desc}</p>
                  <div className="mt-5 flex gap-2">
                    <Link
                      to={p.info}
                      className="inline-flex flex-shrink-0 items-center justify-between gap-3 rounded-full border border-[#E8490F]/45 bg-[#E8490F]/12 px-3.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#E8490F]"
                      data-testid={`platform-info-${p.key}`}
                      data-track={`platform-info-${p.key}`}
                    >
                      {home.infoChip}
                      <ArrowRight size={12} />
                    </Link>
                    <a
                      href={`https://${p.site}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-w-0 flex-1 items-center justify-between gap-2 rounded-full border border-[#3a3a3a] bg-[#141414] px-3.5 py-1.5 text-xs font-medium text-white/85 transition-colors hover:border-[#E8490F] hover:text-white"
                      data-testid={`platform-site-${p.key}`}
                      data-track={`platform-site-${p.key}`}
                    >
                      <span className="truncate">{p.site}</span>
                      <ArrowUpRight size={12} className="flex-shrink-0 text-[#E8490F]" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div></div>
      </section>

      {/* CTA BAND */}
      <section className="pb-12 md:pb-16" data-testid="section-cta-band">
        <div className="mise-container"><div className={NARROW}>
          <div className="rounded-2xl border border-[#E8490F]/35 bg-gradient-to-r from-[#2b1810] via-[#1c1c1c] to-[#1a1a1a] px-7 py-8 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="max-w-2xl">
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">{home.ctaBandHeading}</h2>
              <p className="mt-2 text-white/65 leading-relaxed">{home.ctaBandText}</p>
            </div>
            <Link to="/contact" className="mise-pill-primary self-start md:self-auto whitespace-nowrap" data-testid="cta-band-button" data-track="cta-band">
              {home.ctaBandButton}
              <ArrowRight size={16} />
            </Link>
          </div>
        </div></div>
      </section>
    </div>
  );
};

const Stat = ({ stat }) => (
  <div className="mt-4">
    <p className="text-3xl font-semibold tracking-tight text-[#E8490F]">{stat.value}</p>
    <p className="mt-1 max-w-[16rem] text-sm text-white/65 leading-snug">{stat.label}</p>
  </div>
);

const AnswerChip = ({ to, icon: Icon, label }) => (
  <Link
    to={to}
    data-track={`answer-chip${to.replace("/", "-")}`}
    className="inline-flex items-center gap-1.5 rounded-full border border-[#E8490F]/45 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-[#E8490F]/15"
  >
    <Icon size={13} className="text-[#E8490F]" />
    {label}
  </Link>
);

export default Home;
